import {CommonModule, formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faPen, faTrash} from '@fortawesome/free-solid-svg-icons';
import {ToastrService} from 'ngx-toastr';
import {DatePickerModule} from 'primeng/datepicker';

import {ApiService} from '../../services/api.service';

import {ActivityTimelineComponent} from '../../shared/activity-timeline/activity-timeline.component';

@Component({
    selector: 'app-lead-detail',
    imports: [CommonModule, FormsModule, FontAwesomeModule, DatePickerModule, ActivityTimelineComponent],
    templateUrl: './lead-detail.component.html',
    styleUrls: ['../lead-management.shared.css']
})
export class LeadDetailComponent implements OnInit {
  lead: any = null;
  showFollowUpModal = false;
  showDemoModal = false;
  showConvertPartnerModal = false;
    showRevertPartnerModal = false;
    revertingPartner = false;
  showDeleteLeadModal = false;
  deletingLead = false;
  editingFollowUp: any = null;
  editingDemo: any = null;
  followUpForm: any = { follow_up_type: 'CALL', follow_up_date: '', notes: '', next_follow_up_date: '' };
  demoForm: any = { demo_datetime: '', demo_type: 'ONLINE', demo_status: 'SCHEDULED', demo_notes: '', remind_one_hour_before: false };
  followUpTypes = [['CALL', 'Call'], ['WHATSAPP', 'WhatsApp'], ['EMAIL', 'Email'], ['MEETING', 'Meeting'], ['DEMO', 'Demo'], ['OTHER', 'Other']];
  demoTypes = [['ONLINE', 'Online'], ['ON_SITE', 'On-site']];
  demoStatuses = [['SCHEDULED', 'Scheduled'], ['COMPLETED', 'Completed'], ['CANCELLED', 'Cancelled']];

  constructor(
    private apiService: ApiService,
    private route: ActivatedRoute,
    public router: Router,
    private toastr: ToastrService,
    library: FaIconLibrary,
  ) {
    library.addIcons(faPen, faTrash);
  }

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.loadLead(id);
  }

  loadLead(id: number): void {
    this.apiService.getLead(id).subscribe({
      next: (response: any) => {
        this.lead = response;
      }
    });
  }

  openEditLead(): void {
    if (!this.lead?.id) {
      return;
    }
    this.router.navigate(['lead-management', 'leads', this.lead.id, 'edit']);
  }

  convertToSalesInstallationPartner(): void {
    if (!this.lead?.id) {
      return;
    }
    this.showConvertPartnerModal = true;
  }

    openRevertFromPartner(): void {
        if (!this.lead?.sales_installation_partner) {
            return;
        }
        this.showRevertPartnerModal = true;
    }

    confirmRevertFromPartner(): void {
        const partnerId = this.resolvePartnerId(this.lead?.sales_installation_partner);
        if (!partnerId || this.revertingPartner) {
            return;
        }
        this.revertingPartner = true;
        this.apiService.revertSalesInstallationPartnerToLead(partnerId).subscribe({
            next: () => {
                this.revertingPartner = false;
                this.showRevertPartnerModal = false;
                this.toastr.success('Lead restored to the lead listing.', 'Success');
                this.loadLead(this.lead.id);
            },
            error: () => {
                this.revertingPartner = false;
            },
        });
    }

    private resolvePartnerId(partner: number | { id?: number } | null | undefined): number | null {
        if (partner == null) {
            return null;
        }
        if (typeof partner === 'object') {
            return partner.id ?? null;
        }
        return partner;
    }

  confirmConvertToSalesInstallationPartner(): void {
    if (!this.lead?.id) {
      return;
    }
    this.apiService.convertLeadToSalesInstallationPartner(this.lead.id).subscribe({
      next: (response: any) => {
        this.showConvertPartnerModal = false;
        this.toastr.success('Lead converted successfully.', 'Success');
        this.router.navigate(['admin/sales-installation-partners', response.partner_id]);
      }
    });
  }

  addFollowUp(): void {
    const request = this.editingFollowUp
      ? this.apiService.updateLeadFollowUp(this.lead.id, this.editingFollowUp.id, this.serializeFollowUpForm(this.followUpForm))
      : this.apiService.addLeadFollowUp(this.lead.id, this.serializeFollowUpForm(this.followUpForm));

    request.subscribe({
      next: () => {
        this.showFollowUpModal = false;
        this.editingFollowUp = null;
        this.loadLead(this.lead.id);
      }
    });
  }

  addDemo(): void {
    const request = this.editingDemo
      ? this.apiService.updateLeadDemo(this.lead.id, this.editingDemo.id, this.serializeDemoForm(this.demoForm))
      : this.apiService.addLeadDemo(this.lead.id, this.serializeDemoForm(this.demoForm));

    request.subscribe({
      next: () => {
        this.showDemoModal = false;
        this.editingDemo = null;
        this.loadLead(this.lead.id);
      }
    });
  }

  openAddFollowUp(): void {
    this.editingFollowUp = null;
    this.followUpForm = { follow_up_type: 'CALL', follow_up_date: null, notes: '', next_follow_up_date: null };
    this.showFollowUpModal = true;
  }

  openEditFollowUp(followUp: any): void {
    this.editingFollowUp = followUp;
    this.followUpForm = {
      follow_up_type: followUp.follow_up_type,
      follow_up_date: this.parseDate(followUp.follow_up_date),
      notes: followUp.notes || '',
      next_follow_up_date: this.parseDate(followUp.next_follow_up_date),
    };
    this.showFollowUpModal = true;
  }

  deleteFollowUp(followUp: any): void {
    if (!window.confirm('Delete this follow-up?')) {
      return;
    }
    this.apiService.deleteLeadFollowUp(this.lead.id, followUp.id).subscribe({
      next: () => {
        this.toastr.success('Follow-up deleted successfully.', 'Success');
        this.loadLead(this.lead.id);
      }
    });
  }

  openAddDemo(): void {
    this.editingDemo = null;
    this.demoForm = { demo_datetime: null, demo_type: 'ONLINE', demo_status: 'SCHEDULED', demo_notes: '', remind_one_hour_before: false };
    this.showDemoModal = true;
  }

  openEditDemo(demo: any): void {
    this.editingDemo = demo;
    this.demoForm = {
      demo_datetime: this.parseDate(demo.demo_datetime),
      demo_type: demo.demo_type,
      demo_status: demo.demo_status,
      demo_notes: demo.demo_notes || '',
      remind_one_hour_before: !!demo.remind_one_hour_before,
    };
    this.showDemoModal = true;
  }

  deleteDemo(demo: any): void {
    if (!window.confirm('Delete this demo?')) {
      return;
    }
    this.apiService.deleteLeadDemo(this.lead.id, demo.id).subscribe({
      next: () => {
        this.toastr.success('Demo deleted successfully.', 'Success');
        this.loadLead(this.lead.id);
      }
    });
  }

  openDeleteLeadModal(): void {
    if (!this.lead?.id) {
      return;
    }
    this.showDeleteLeadModal = true;
  }

  closeDeleteLeadModal(): void {
    if (this.deletingLead) {
      return;
    }
    this.showDeleteLeadModal = false;
  }

  confirmDeleteLead(): void {
    if (!this.lead?.id || this.deletingLead) {
      return;
    }
    this.deletingLead = true;
    this.apiService.deleteLead(this.lead.id).subscribe({
      next: () => {
        this.deletingLead = false;
        this.showDeleteLeadModal = false;
        this.toastr.success('Lead deleted successfully.', 'Success');
        this.router.navigate(['lead-management/leads']);
      },
      error: (err) => {
        this.deletingLead = false;
        const message = err?.error?.detail || 'Unable to delete lead.';
        this.toastr.error(message, 'Error');
      }
    });
  }

  private parseDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private serializeDate(value: Date | string | null | undefined): string {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    return formatDate(date, 'yyyy-MM-dd', 'en-IN');
  }

  private serializeDateTime(value: Date | string | null | undefined): string {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    return formatDate(date, "yyyy-MM-dd'T'HH:mm:ss", 'en-IN');
  }

  private serializeFollowUpForm(form: any): any {
    return {
      ...form,
      follow_up_date: this.serializeDate(form.follow_up_date),
      next_follow_up_date: form.next_follow_up_date ? this.serializeDate(form.next_follow_up_date) : null,
    };
  }

  private serializeDemoForm(form: any): any {
    return {
      ...form,
      demo_datetime: this.serializeDateTime(form.demo_datetime),
    };
  }
}
